import { Button } from '@/components/Button';
import DropdownMenu from '@/components/DropdownMenu';
import Text from '@/components/Text';
import { cn } from '@/lib/cn';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RollUpPluginDisplayName, RollUpPluginId } from '@squaredup/ids';
import LoadingSpinner from 'components/LoadingSpinner';
import NoDataPlaceholder from 'components/NoDataPlaceholder';
import Tooltip from 'components/tooltip/Tooltip';
import { useDashboardContext } from 'contexts/DashboardContext';
import { useDataStreamWorkspaceContext } from 'contexts/DataStreamWorkspaceContext';
import { sortBy } from 'lodash';
import { CreateEditScope } from 'pages/scope/CreateEditScope';
import { useAvailableDataStreamDefinitionsForWorkspace } from 'queries/hooks/useAvailableDataStreamDefinitionsForWorkspace';
import { scopeQueryKeys } from 'queries/queryKeys/scopeKeys';
import { variableObjectQueryKeys } from 'queries/queryKeys/variableObjectKeys';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { NO_ACTIVE_WORKSPACE } from 'services/WorkspaceUtil';
import { EditorSteps } from '../constants';
import { useTileEditorStepsContext } from '../contexts/TileEditorStepsContext';
import { useDataSources } from '../hooks/useDataSources';
import { useRecentDataStreamStore } from '../hooks/useRecentDataStreamStore';
import { useTileEditorScopes } from '../hooks/useScopes';
import { useTileEditorStore } from '../state/TileEditorStoreProvider';
import { useTileEditorDataStreams } from '../state/useTileEditorDataStreams';
import { AddDataSourceModal } from '../steps/stepDefinitions/dataStreamStep/dataSourceModal/AddDataSourceModal.tsx';
import { SplashScreenDatasourceButton } from './SplashScreenDatasourceButton';
import { SplashScreenDataStreamOption } from './SplashScreenDataStreamOption';
import { SplashScreenOptionsDropdown } from './SplashScreenOptionsDropdown';
import { SplashScreenOptionsDropdownOption } from './SplashScreenOptionsDropdownOption';
import { SplashScreenScopeOption } from './SplashScreenScopeOption';
import { useSplashScreenDatasources } from './useSplashScreenDatasources.ts';

interface BrowseAllDataStreamsButtonProps {
    className?: string;
}

const BrowseAllDataStreamsButton: React.FC<BrowseAllDataStreamsButtonProps> = ({ className }) => {
    const { setCurrentEditorStep } = useTileEditorStepsContext();

    return (
        <Button
            onClick={() => setCurrentEditorStep(EditorSteps.dataStream)}
            className={cn(className)}
            variant='secondary'
        >
            Browse all data streams
        </Button>
    );
};

const RollUpTooltipText = () => (
    <>
        RollUp gives a high-level view of health across workspaces and dashboards. Ideal for stakeholders to see status
        at a glance.{' '}
        <a
            href='https://docs.squaredup.com/first-steps/rollup'
            target='_blank'
            rel='noreferrer'
            className='text-textLink hover:underline'
        >
            Learn more
        </a>
    </>
);

export const SplashScreen = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { isDocked, currentEditorStep, setCurrentEditorStep } = useTileEditorStepsContext();
    const { workspace } = useDataStreamWorkspaceContext();
    const { variables = [] } = useDashboardContext();

    const { dispatch } = useTileEditorStore((s) => ({ dispatch: s.dispatch }));

    const { recentDataStreams: recentDataStreamsIds } = useRecentDataStreamStore();
    const [isAddDataSourceModal, setIsAddDataSourceModal] = useState(false);
    const [isScopeModalOpen, setIsScopeModalOpen] = useState(false);

    let { data: datasources = [], isLoading: isLoadingDataSources } = useDataSources({}, true, workspace);

    const { data: allTenantDatasources = [], isLoading: isLoadingAllTenantDataSources } = useDataSources(
        {},
        true,
        NO_ACTIVE_WORKSPACE
    );

    const { data: tenantUserAddedDatasources = [] } = useDataSources({}, false, NO_ACTIVE_WORKSPACE);

    const { data: tileEditorDataStreams, isLoading: isLoadingTileEditorDataStreams } =
        useTileEditorDataStreams(workspace);
    const { data: dataStreams = [], isLoading: isLoadingAvailableDataStreamDefinitionsForWorkspace } =
        useAvailableDataStreamDefinitionsForWorkspace(datasources, workspace, undefined);

    const { data: scopes = [], isLoading: isLoadingScopes } = useTileEditorScopes();

    const isLoading =
        isLoadingDataSources ||
        isLoadingAllTenantDataSources ||
        isLoadingTileEditorDataStreams ||
        isLoadingAvailableDataStreamDefinitionsForWorkspace ||
        isLoadingScopes;

    // Get list of datasources from available data streams
    const splashScreenDatasources = useSplashScreenDatasources(datasources, dataStreams);
    // Find plugins that have only failing datasource instances
    const failingPluginIds = splashScreenDatasources
        .filter((ds) => ds.failedImports && ds.failedImports.length > 0)
        .map((ds) => ds.id);

    const recentDataStreams = sortBy(
        dataStreams.filter(
            ({ id, pluginId }) =>
                id !== 'datastream-sql' && recentDataStreamsIds.includes(id) && !failingPluginIds.includes(pluginId)
        ),
        ({ id }) => recentDataStreamsIds.indexOf(id)
    );

    const variablesScopeIds = variables.map((v) => ('scope' in v.scope ? v.scope.scope : undefined));
    const aboveFoldCount = 7;
    const belowFoldDatasources = splashScreenDatasources.slice(aboveFoldCount);
    const aboveFoldDatasources = [
        ...splashScreenDatasources.slice(0, aboveFoldCount),
        /**
         * Always show rollup datasource at end of above the fold list.
         * If tenant has not added any datasource beforehand, then the rollup datasource is hidden.
         * This is because, rollup is not useful without any dashboards/monitors/workspaces.
         */
        ...(tenantUserAddedDatasources.length > 0
            ? [
                  {
                      id: RollUpPluginId.value,
                      name: RollUpPluginDisplayName,
                      tooltipText: <RollUpTooltipText />
                  }
              ]
            : [])
    ];

    /**
     * Calculate the number of recent datastreams and scopes to show
     * We show a min of 4 and a max of 6 items. We show a number that is proportional to the number of
     * shown data sources. i.e. if there are 8 data sources being shown, we'd show up to 6 recent data streams
     * and or scopes. This used to allow a lower min and higher max so is overly complicated given what it's calculating
     */
    const availableOptionsCount = Math.min(Math.max(Math.ceil((aboveFoldDatasources.length + 1) / 3) * 2, 4), 6);
    const evenDistribution = availableOptionsCount / 2;

    const [dataStreamsToBeShown, scopesToBeShown] =
        scopes.length >= evenDistribution && recentDataStreams.length >= evenDistribution
            ? [evenDistribution, evenDistribution]
            : recentDataStreams.length > scopes.length
            ? [availableOptionsCount - scopes.length, scopes.length]
            : [recentDataStreams.length, availableOptionsCount - recentDataStreams.length];

    const belowFoldScopes = scopes
        .filter((scope) => variablesScopeIds.includes(scope.id))
        .map(({ id, displayName }) => ({ id, name: displayName }));
    const aboveFoldScopes = belowFoldScopes.splice(0, scopesToBeShown);

    const hasDatasources = Boolean(aboveFoldDatasources.length);
    const hasDataStreams = Boolean(recentDataStreams?.length);
    const hasScopes = Boolean(aboveFoldScopes.length);

    if (isLoading) {
        return (
            <div className='flex items-center justify-center w-full h-full'>
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <>
            <div
                className='relative flex flex-col justify-center w-full h-full min-w-0 min-h-0 isolate'
                id='dataStreamTileSpashScreen'
            >
                <div className='flex flex-col items-center w-full min-h-0 mx-auto max-w-7xl'>
                    <NoDataPlaceholder message="Let's get started" className='text-textPrimary' />

                    {!isDocked && hasDatasources && (
                        <>
                            <div
                                className={cn(
                                    'flex w-full mt-8 mb-10 items-center max-w-6xl @container/datasources justify-center gap-10'
                                )}
                            >
                                <div
                                    className={cn(
                                        'flex flex-row flex-wrap justify-center gap-3 @4xl/datasources:gap-5 max-w-[321px] @4xl/datasources:max-w-[395px]',
                                        [6, 7].includes(aboveFoldDatasources.length) &&
                                            '@4xl/datasources:max-w-[533px]',
                                        aboveFoldDatasources.length === 3 &&
                                            'max-w-[258px] @4xl/datasources:max-w-[258px]'
                                    )}
                                >
                                    {aboveFoldDatasources.map(({ id, name, tooltipText, failedImports }) => (
                                        <SplashScreenDatasourceButton
                                            key={id}
                                            id={id}
                                            name={name}
                                            tooltipText={tooltipText}
                                            dataStreams={dataStreams}
                                            tileEditorDataStreams={tileEditorDataStreams}
                                            failedImports={failedImports}
                                        />
                                    ))}

                                    <div
                                        className={cn(
                                            'grid gap-3 @4xl/datasources:gap-5 w-[100px] @4xl/datasources:w-[120px]',
                                            belowFoldDatasources.length && 'grid-rows-2'
                                        )}
                                    >
                                        {Boolean(belowFoldDatasources.length) && (
                                            <SplashScreenOptionsDropdown
                                                options={belowFoldDatasources}
                                                option={({ id, name, failedImports }) => (
                                                    <SplashScreenOptionsDropdownOption
                                                        key={id}
                                                        id={id}
                                                        name={name}
                                                        dataStreams={dataStreams}
                                                        tileEditorDataStreams={tileEditorDataStreams}
                                                        failedImports={failedImports}
                                                    />
                                                )}
                                            />
                                        )}

                                        <Tooltip title='Add data source'>
                                            <button
                                                key='addDatasource'
                                                className='flex items-center justify-center w-full h-full px-5 py-3.5 space-y-3 rounded-md text-textSecondary bg-tileEditorDataset hover:bg-tileEditorDatasetActive focus:bg-tileEditorDatasetActive'
                                                onClick={() => {
                                                    setIsAddDataSourceModal(true);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faPlus} size='2x' />
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>

                                {hasDatasources && (hasDataStreams || hasScopes) && (
                                    <div className='flex flex-col flex-1 max-w-md min-w-0 gap-10 pl-10 my-auto border-l border-dividerTertiary'>
                                        {hasDataStreams && (
                                            <div className='flex flex-col gap-2 flex-1 min-w-[200px] max-w-[340px]'>
                                                <Text.H5 className='text-textDisabled'>Recent</Text.H5>

                                                {recentDataStreams.slice(0, dataStreamsToBeShown).map((dataStream) => (
                                                    <SplashScreenDataStreamOption
                                                        key={dataStream.id}
                                                        dataStream={dataStream}
                                                        dataStreamInstance={tileEditorDataStreams.dataStreamsById?.get(
                                                            dataStream.id
                                                        )}
                                                    />
                                                ))}
                                            </div>
                                        )}

                                        {Boolean(aboveFoldScopes.length) && (
                                            <div
                                                className='flex flex-col gap-2 flex-1 min-w-[200px] max-w-[340px]'
                                                data-testid='splashScreenCollections'
                                            >
                                                <Text.H5 className='flex items-center text-textDisabled'>
                                                    Dashboard Variable
                                                </Text.H5>

                                                {aboveFoldScopes.map((scope) => (
                                                    <SplashScreenScopeOption
                                                        key={scope.id}
                                                        id={scope.id}
                                                        name={scope.name}
                                                        workspace={workspace}
                                                        variables={variables}
                                                        isVariable={variablesScopeIds.includes(scope.id)}
                                                        dispatch={dispatch}
                                                        setCurrentEditorStep={setCurrentEditorStep}
                                                    />
                                                ))}

                                                {Boolean(belowFoldScopes.length) && (
                                                    <SplashScreenOptionsDropdown
                                                        options={belowFoldScopes}
                                                        className='py-1.5 h-[35px] rounded-none'
                                                        option={({ id, name }) => (
                                                            <DropdownMenu.Item
                                                                key={id}
                                                                onSelect={() => {
                                                                    dispatch({
                                                                        type: 'dataStream.updateFilters',
                                                                        filters: {
                                                                            scopeId: id
                                                                        }
                                                                    });

                                                                    dispatch({
                                                                        type: 'setPredefinedScope',
                                                                        scopeId: id,
                                                                        workspaceId: workspace,
                                                                        variables
                                                                    });

                                                                    return setCurrentEditorStep(EditorSteps.dataStream);
                                                                }}
                                                                className='text-nowrap'
                                                            >
                                                                {name}
                                                            </DropdownMenu.Item>
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <BrowseAllDataStreamsButton className='mx-auto mt-8 text-base' />
                        </>
                    )}

                    {currentEditorStep !== EditorSteps.dataStream && !hasDatasources && (
                        <div className='flex-col justify-center mt-5 space-y-10 text-center'>
                            <p className='text-base text-textSecondary'>
                                Add a data source to start surfacing insights.
                            </p>

                            <Button
                                onClick={() =>
                                    allTenantDatasources.length
                                        ? setIsAddDataSourceModal(true)
                                        : navigate(`datasources/${workspace}`)
                                }
                                className='mx-auto mt-10 text-base'
                                icon={<FontAwesomeIcon icon={faPlus} />}
                            >
                                Add data source
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {isAddDataSourceModal && (
                <AddDataSourceModal
                    workspaceId={workspace}
                    onSelectedDataSource={async (dataSourceId?: string) => {
                        setIsAddDataSourceModal(false);

                        if (dataSourceId) {
                            // Remove the search to avoid getting stuck on the empty page
                            dispatch({
                                type: 'dataStream.updateFilters',
                                filters: { search: '', ...(dataSourceId ? { dataSourceId } : {}) }
                            });

                            setCurrentEditorStep(EditorSteps.dataStream);
                        }
                    }}
                />
            )}

            {isScopeModalOpen && (
                <CreateEditScope
                    onClose={(modifiedScope) => {
                        setIsScopeModalOpen(false);
                        if (modifiedScope && workspace) {
                            queryClient.invalidateQueries(scopeQueryKeys.workspace(workspace));
                            queryClient.removeQueries(variableObjectQueryKeys.all);
                            dispatch({ type: 'dataStream.updateFilters', filters: { scopeId: modifiedScope.id } });
                            setCurrentEditorStep(EditorSteps.dataStream);
                        }
                    }}
                />
            )}
        </>
    );
};
